import { Button } from '@1trading/react';
import { Loader2 } from 'lucide-react';
import { type ReactNode, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';

type RequireAuthProps = {
  children: ReactNode;
};

const AuthScreen = ({ children }: { children: ReactNode }) => (
  <main className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-background px-6 text-center text-foreground">{children}</main>
);

const Spinner = () => <Loader2 aria-hidden className="size-6 animate-spin text-muted-foreground" />;

/**
 * Gates its children behind a valid Keycloak session. Unauthenticated visitors
 * are sent straight to Keycloak (Authorization Code + PKCE); the loading and
 * error branches keep the SPA from flashing protected content pre-auth.
 */
export const RequireAuth = ({ children }: RequireAuthProps) => {
  const auth = useAuth();

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator && !auth.error) {
      void auth.signinRedirect();
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.error, auth.signinRedirect]);

  if (auth.error) {
    return (
      <AuthScreen>
        <h1 className="font-semibold text-2xl">Sign-in failed</h1>
        <p className="max-w-md text-muted-foreground text-sm">{auth.error.message}</p>
        <Button onClick={() => void auth.signinRedirect()}>Try again</Button>
      </AuthScreen>
    );
  }

  if (auth.isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <AuthScreen>
      <Spinner />
      <p className="text-muted-foreground text-sm">{auth.activeNavigator ? 'Completing sign in…' : 'Redirecting to sign in…'}</p>
    </AuthScreen>
  );
};
